// Campo "Zona" del carrello: input compatto con menu a tendina proprio (non il datalist del browser,
// che filtra sul valore già scritto e nasconde le altre zone).
// - al click mostra TUTTE le zone; filtra solo quando si digita
// - scegliere una voce salva subito; Invio salva il testo; Esc annulla
// - in modifica il campo si allarga sopra le colonne vicine (la colonna resta stretta)
import React, { useEffect, useRef, useState } from 'react';
import { normalizeZone } from './cartZones';

// Zona: massimo 100 caratteri; in modifica il campo si allarga quanto il nome più lungo (max 100).
const MAX_ZONE_CHARS = 100;

const ZoneSelect = ({ value, zones = [], onCommit, placeholder, noZoneLabel, newZoneLabel = "Nuova zona", clearLabel = "Cancella zona", width = 95 }) => {
  // width: numero (px) o misura CSS (es. "34ch" = larghezza a riposo calcolata sulle zone del carrello).
  const cssWidth = typeof width === "number" ? `${width}px` : width;
  const current = normalizeZone(value);
  const [text, setText] = useState(current);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [menuPos, setMenuPos] = useState(null);
  // Spazio disponibile fino al bordo destro della finestra: il campo non deve uscire dallo schermo
  // (il browser scorrerebbe la pagina per seguire il cursore).
  const [maxAvail, setMaxAvail] = useState(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const skipBlurCommit = useRef(false);

  // Valore cambiato da fuori (es. riordino, caricamento ordine) mentre il campo non è attivo.
  useEffect(() => {
    if (!open) setText(current);
  }, [current, open]);

  const query = normalizeZone(text).toLowerCase();
  const longest = Math.max(text.length, ...zones.map((zone) => zone.length), 8);
  const expandedWidth = `${Math.min(longest, MAX_ZONE_CHARS) + 3}ch`;
  const typedZone = normalizeZone(text);
  const isNewZone = typed && !!typedZone && !zones.some((zone) => zone.toLowerCase() === typedZone.toLowerCase());
  const options = [
    // Nome nuovo: voce per confermarlo, così il menu non sparisce quando nessuna zona corrisponde.
    ...(isNewZone ? [{ label: `${newZoneLabel}: «${typedZone}»`, value: typedZone, isNew: true }] : []),
    ...(current ? [{ label: noZoneLabel, value: '', empty: true }] : []),
    ...zones
      .filter((zone) => !typed || !query || zone.toLowerCase().includes(query))
      .map((zone) => ({ label: zone, value: zone })),
  ];

  const placeMenu = () => {
    const rect = inputRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
    setMaxAvail(Math.max(rect.width, window.innerWidth - rect.left - 16));
  };

  const commit = (next) => {
    const normalized = normalizeZone(next).slice(0, MAX_ZONE_CHARS);
    setText(normalized);
    if (normalized !== current) onCommit(normalized);
  };

  const close = () => {
    setOpen(false);
    setTyped(false);
    setHighlight(-1);
  };

  const choose = (option) => {
    skipBlurCommit.current = true;
    commit(option.value);
    close();
    inputRef.current?.blur();
  };

  // Il campo si allarga mentre si scrive: riposiziona il menu (in posizione fissa).
  useEffect(() => {
    if (!open) return undefined;
    const raf = requestAnimationFrame(placeMenu);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, text]);

  // Se la pagina scorre o la finestra cambia, il menu segue il campo (prima si chiudeva e si perdeva il focus).
  useEffect(() => {
    if (!open) return undefined;
    const onScroll = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      placeMenu();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  return (
    <div className="zone-select" style={{ width: cssWidth }}>
      {/* Il contenitore del campo si allarga in modifica: la × resta sul bordo destro del campo. */}
      <div
        className={`zone-select-field${open ? ' is-open' : ''}`}
        style={open ? { width: `min(max(${cssWidth}, ${expandedWidth}), ${maxAvail ? `${maxAvail}px` : '100vw'})` } : undefined}
      >
      <input
        ref={inputRef}
        type="text"
        className={`zone-select-input${open ? ' is-open' : ''}`}
        maxLength={MAX_ZONE_CHARS}
        value={text}
        title={current || undefined}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={(e) => {
          setOpen(true);
          setTyped(false);
          setHighlight(-1);
          e.target.select();
        }}
        onChange={(e) => {
          setText(e.target.value);
          setTyped(true);
          setHighlight(-1);
        }}
        onBlur={() => {
          if (skipBlurCommit.current) {
            skipBlurCommit.current = false;
          } else {
            commit(text);
          }
          close();
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, options.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, -1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlight >= 0 && options[highlight]) choose(options[highlight]);
            else inputRef.current?.blur();
          } else if (e.key === 'Escape') {
            skipBlurCommit.current = true;
            setText(current);
            close();
            inputRef.current?.blur();
          }
        }}
      />
      {(open ? text : current) && (
        <button
          type="button"
          className="zone-select-clear"
          title={clearLabel}
          aria-label={clearLabel}
          tabIndex={-1}
          // mousedown: agisce senza far perdere il focus al campo.
          onMouseDown={(e) => {
            e.preventDefault();
            if (open) {
              // In modifica: svuota il testo e resta nel campo per scriverne un altro.
              setText('');
              setTyped(true);
              setHighlight(-1);
            } else {
              // Campo chiuso: toglie subito la zona dalla riga.
              commit('');
            }
          }}
        >
          ×
        </button>
      )}
      </div>
      {open && menuPos && options.length > 0 && (
        <ul ref={menuRef} className="zone-select-menu" style={{ top: menuPos.top, left: menuPos.left, minWidth: menuPos.width, maxWidth: `min(${MAX_ZONE_CHARS + 4}ch, calc(100vw - ${Math.round(menuPos.left) + 16}px))` }}>
          {options.map((option, idx) => (
            <li
              key={option.empty ? '__none__' : option.isNew ? '__new__' : option.value}
              className={[
                'zone-select-option',
                option.empty ? 'is-empty' : '',
                option.isNew ? 'is-new' : '',
                idx === highlight ? 'is-active' : '',
                !option.empty && option.value.toLowerCase() === current.toLowerCase() ? 'is-selected' : '',
              ].join(' ')}
              // mousedown (non click): sceglie prima che l'input perda il focus.
              onMouseDown={(e) => {
                e.preventDefault();
                choose(option);
              }}
              onMouseEnter={() => setHighlight(idx)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ZoneSelect;
